import React from 'react'
import { Table2, Column, Cell } from '@blueprintjs/table'

// Utility function to determine background color for a value
/**
 * Returns a background color string based on the given value.
 * @param {number} value - The value to evaluate.
 * @returns {string} A hex color code.
 */
function getColorForValue(value) {
  if (value >= 70) return '#6aa84f'
  if (value >= 50) return '#b6d7a8'
  if (value >= 30) return '#ffe599'
  if (value >= 15) return '#ea9999'
  return '#e06666'
}

/**
 * SegmentationsScoresTable renders a table displaying segmentation scores.
 * @param {Object} props - Component props.
 * @param {Array} props.segmentationScores - Array of segmentation score objects.
 * @returns {JSX.Element|null} The rendered table element or null if no scores.
 */
export function SegmentationsScoresTable({ segmentationScores }) {
  if (!segmentationScores || segmentationScores.length === 0) return null

  // Sort segmentation scores in descending order and extract headers
  const sortedScores = [...segmentationScores].sort(
    (a, b) => b.segmentation_score - a.segmentation_score
  )
  const columns = sortedScores.map(score => score.name)
  const headers = ['Priority', 'Weight', ...columns]

  // Build rows directly from the ordered dataframe
  const firstSegmentation = sortedScores[0]
  const dataframe = firstSegmentation.dataframe

  const rows = Object.entries(dataframe).map(([priorityKey, priorityData]) => [
    priorityData.priority_name,
    priorityData.priority_weight,
    ...sortedScores.map(score => {
      const scoreData = score.dataframe[priorityKey]
      return scoreData
        ? (scoreData.final_differentiator_score * 100).toFixed(2)
        : 'N/A'
    }),
  ])

  // Add segmentation score row at the end
  rows.push([
    'Segmentation Score',
    '---',
    ...sortedScores.map(
      score => (score.segmentation_score * 100)?.toFixed(2) || 'N/A'
    ),
  ])

  return (
    <div style={{ textAlign: 'center', marginTop: 15 }}>
      <Table2
        numRows={rows.length}
        enableRowHeader={false}
        enableColumnResizing={true}
        defaultRowHeight={30}
        numFrozenColumns={2}
        style={{ tableLayout: 'auto', width: '100%', margin: '0 auto' }}
      >
        {headers.map((header, colIndex) => (
          <Column
            key={header}
            name={<div style={{ textAlign: 'center' }}>{header}</div>}
            cellRenderer={rowIndex => {
              const cellValue = rows[rowIndex][colIndex]
              const isNumeric =
                typeof cellValue === 'number' ||
                (typeof cellValue === 'string' &&
                  /^[+-]?(\d+(\.\d+)?)$/.test(cellValue))
              const numericValue = isNumeric ? parseFloat(cellValue) : NaN
              const cellStyle = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor:
                  header === 'Weight'
                    ? '#E4E0E1'
                    : isNumeric
                    ? getColorForValue(numericValue)
                    : undefined,
                color: isNumeric ? 'black' : undefined,
              }

              // For 'Weight', do not append '%'
              const cellContent =
                header === 'Weight'
                  ? cellValue
                  : isNumeric
                  ? numericValue + ' %'
                  : cellValue

              return <Cell style={cellStyle}>{cellContent}</Cell>
            }}
          />
        ))}
      </Table2>
    </div>
  )
}
